/**
 * @file    ProjectCard.js
 * @author  Daniel Naftzger
 * @created 2026-02-26
 * @updated 2026-03-05
 *
 * @description
 * Card component for displaying an individual project. Shows the project
 * name, year, description, and technology tags. Includes a hover effect
 * that highlights the card border and reveals a navigation arrow.
 *
 * If the project has an `architecture` field in data.js, an
 * "About this Project" button is rendered that opens the architecture modal.
 */
import { useState } from 'react';
import styles from '../styles';

/**
 * Renders a clickable project card with hover animations.
 *
 * @param {Object}   props
 * @param {Object}   props.project           - Project data from data.js
 * @param {string}   props.project.name      -
 * @param {string}   props.project.year      -
 * @param {string}   props.project.desc      -
 * @param {string[]} props.project.tags      -
 * @param {string}   props.project.link      -
 * @param {Object}   [props.project.architecture] - Optional architecture data
 * @param {Function} [props.onArchClick]     - Called with the project when the architecture button is clicked
 * @returns {JSX.Element}
 */
function ProjectCard({ project, onArchClick }) {
  const [hovered, setHovered] = useState(false);
  const [archBtnHovered, setArchBtnHovered] = useState(false);
  const hasArch = !!project.architecture;

  return (
    <a
      href={project.link}
      className="project-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles.projectCard,
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        borderColor: hovered ? "#e8ff47" : "#1e1e1e",
        transition: "all 0.25s ease",
      }}
    >
      <div style={styles.projectTop}>
        <span style={styles.projectYear}>{project.year}</span>
        <span
          style={{
            ...styles.projArrow,
            opacity: hovered ? 1 : 0.3,
            transform: hovered ? "translate(2px,-2px)" : "none",
            transition: "all 0.2s ease",
          }}
        >
          ↗
        </span>
      </div>
      <div style={styles.projectName}>{project.name}</div>
      <p style={styles.projectDesc}>{project.desc}</p>
      <div style={styles.projectTags}>
        {project.tags.map((t, i) => (
          <span key={i} style={styles.projectTag}>{t}</span>
        ))}
      </div>

      {/* Architecture button — only rendered if architecture data exists */}
      {hasArch && (
        <button
          style={{
            ...styles.archBtn,
            background: archBtnHovered ? "rgba(232,255,71,0.08)" : "transparent",
            borderColor: archBtnHovered ? "rgba(232,255,71,0.6)" : "rgba(232,255,71,0.3)",
          }}
          onMouseEnter={() => setArchBtnHovered(true)}
          onMouseLeave={() => setArchBtnHovered(false)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onArchClick) onArchClick(project);
          }}
        >
          About this Project →
        </button>
      )}
    </a>
  );
}

export default ProjectCard;